<template>
  <div class="modal">
    <div>
      <div class="bulk-targets">
        <h1>{{ parseTranslation(TITLE) }}</h1>
      </div>

      <p class="instruction">
        {{ parseTranslation(INSTRUCTION) }}
      </p>

      <div class="area-container">
        <div class="single">
          <p>{{ parseTranslation(CODES) }}</p>

          <textarea
            cols="5"
            rows="10"
            class="code-area"
            name="codes"
            :value="values.codes"
            @change="handleValuesChange"
            :placeholder="parseTranslation(ENTER_THE_CODES)"
          ></textarea>
        </div>

        <div class="single">
          <p>{{ parseTranslation(TEXT) }}</p>

          <textarea
            cols="25"
            rows="10"
            :value="values.labels"
            name="labels"
            @change="handleValuesChange"
            :placeholder="parseTranslation(ENTER_THE_LABELS)"
            ref="labelInput"
          ></textarea>
        </div>
      </div>

      <div class="buttons">
        <button @click="cancel">{{ parseTranslation(CANCEL) }}</button>

        <button @click="commitChanges">{{ parseTranslation(SAVE) }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { INDEX_TO_HUMAN_OFFSET } from '@/utils/constants'

import * as translationBulkAddAndEditModal from './BulkAddAndEditModal.translations'

const { default: translations, ...translationKeys } = translationBulkAddAndEditModal

const BULK_TARGETS = {
  CODE: 'code',
  LABEL: 'label',
}

export default {
  name: 'BulkAddAndEditModal',

  props: {
    alternatives: { required: true, type: Array },
    toggleDisplayModalAddOrEditInBulk: { required: true, type: Function },
    updateAlternatives: { required: true, type: Function },
  },

  data() {
    return {
      BULK_TARGETS,

      values: {
        codes: '',
        labels: '',
      },

      translations,
      ...translationKeys,
    }
  },

  computed: {
    buttonsExtractors() {
      const buttonsExtractors = [
        {
          type: BULK_TARGETS.CODE,
          label: this.parseTranslation(this.CODES),
        },

        {
          type: BULK_TARGETS.LABEL,
          label: this.parseTranslation(this.TEXT),
        },
      ]

      return buttonsExtractors
    },
  },

  methods: {
    toggleBulkTargets(type) {
      this.type = type

      this.formatCodesOrLabels()

      this.focusBulkTextarea()
    },

    transformAlternativesInStrings() {
      const [codes, labels] = ['code', 'label'].map(prop =>
        this.alternatives.map(alternative => alternative[prop]).join('\n'),
      )

      this.values = { codes, labels }
    },

    extractArrayFromString(string) {
      const onlyTruthyValues = string.split('\n').filter(Boolean)

      return onlyTruthyValues
    },

    getCodeAndLabelArray() {
      const { codes, labels } = this.values

      return {
        codes: this.extractArrayFromString(codes),
        labels: this.extractArrayFromString(labels),
      }
    },

    getAlternativesFromCodes({ codes, labels }) {
      const newAlternatives = codes.map((code, index) => ({
        code,
        label: labels[index] || `NO LABEL ${index}`,
      }))

      return newAlternatives
    },

    getAlternativesFromLabels({ codes, labels }) {
      const newAlternatives = labels.map((label, index) => ({
        label,
        code: codes[index] || index + INDEX_TO_HUMAN_OFFSET,
      }))

      return newAlternatives
    },

    commitChanges() {
      const { codes, labels } = this.getCodeAndLabelArray()

      const biggest = codes.length > labels.length ? codes : labels
      const getter =
        biggest === codes ? this.getAlternativesFromCodes : this.getAlternativesFromLabels

      this.updateAlternatives(getter({ codes, labels }))

      this.cancel()
    },

    cancel() {
      this.toggleDisplayModalAddOrEditInBulk()
    },

    focusBulkTextarea() {
      this.$refs.labelInput.focus()
    },

    handleValuesChange({ target: { value, name } }) {
      this.values = {
        ...this.values,
        [name]: value,
      }
    },
  },

  created() {
    this.transformAlternativesInStrings()

    const HALF_SECOND = 500

    // when the modal is rendered,
    // the ref still hasn't captured the information from the textarea to place the focus.
    setTimeout(() => {
      this.focusBulkTextarea()
    }, HALF_SECOND)
  },
}
</script>

<style lang="scss" scoped>
.area-container {
  display: flex;
  gap: 12px;

  .single p {
    margin: 12px 0 4px 6px;

    .light & {
      color: $gray-6;
    }
  }
}

.bulk-targets {
  h1 {
    color: $blue-2;
    font-size: rem(22);
    margin-bottom: rem(16);
    font-weight: 600;
  }
}

.modal {
  @include background-modal-config;
  display: flex;

  > div {
    margin: 0 10px;
    padding: 20px;
    border-radius: 3px;
    position: fixed;
    max-width: 80%;
    background: $gray-7;
    @include tipography(16);
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.4);
    display: flex;
    flex-direction: column;

    .instruction {
      @include tipography(16);
      margin-bottom: 5px;

      .light & {
        color: $gray-6;
      }
    }

    textarea {
      @include tipography(20);
      padding: 5px 12px;
      border: 1px solid #707070;
      border-radius: 3px;
      outline: none;
      resize: none;

      .light & {
        color: $gray-6;
      }

      overflow-y: auto;

      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-track {
        background: $gray-light-12;
        border-radius: 5px;
      }

      &::-webkit-scrollbar-thumb {
        background: $gray-13;
        border-radius: 5px;
      }

      &::-webkit-scrollbar-thumb:hover {
        background: $gray-14;
      }
    }

    .buttons {
      @include flexAlignCenter;
      margin: 20px 0 0 auto;

      button {
        @include transparentButton;
        @include tipography(16);
        background: $blue;
        border: 1px solid $blue;
        border-radius: 3px;
        padding: 5px 10px;
        width: 85px;

        &:first-of-type {
          margin-right: 20px;
          border: 1px solid $pink;
          color: $pink;
          background: transparent;
        }

        &:hover {
          transform: scale(1.01);
        }
      }
    }

    .light & {
      background: $white;
    }
  }
}
</style>
